import { useEffect, useMemo, useState, Fragment } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, Layers, ClipboardList, ChevronDown, ChevronRight, Package } from 'lucide-react'
import { supabase, isDemoMode } from '../../lib/supabase'
import { DemoNotice } from '../../lib/demo'

function fmtMoney(n) {
  return '£' + Number(n || 0).toFixed(2)
}

export default function Reports() {
  const [groups, setGroups] = useState([])
  const [groupId, setGroupId] = useState('')
  const [sites, setSites] = useState([])
  const [orders, setOrders] = useState([])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [loading, setLoading] = useState(false)
  const [expandedSite, setExpandedSite] = useState(null)
  const [productFilter, setProductFilter] = useState('__all__')

  // Load groups on mount
  useEffect(() => {
    if (isDemoMode) return
    supabase.from('client_groups').select('id, name').is('archived_at', null).order('name')
      .then(({ data }) => {
        const list = data || []
        setGroups(list)
        if (list.length > 0 && !groupId) setGroupId(list[0].id)
      })
  }, [])

  // When group changes, load its sites + their orders
  useEffect(() => {
    if (!groupId || isDemoMode) { setSites([]); setOrders([]); return }
    setLoading(true)

    const run = async () => {
      const { data: companies } = await supabase
        .from('companies').select('id, name')
        .eq('client_group_id', groupId)
        .is('archived_at', null)
        .order('name')
      setSites(companies || [])
      const companyIds = (companies || []).map(c => c.id)
      if (companyIds.length === 0) { setOrders([]); setLoading(false); return }

      let query = supabase
        .from('orders')
        .select('id, reference, status, placed_at, created_at, customer_po, delivery_date_requested, company_id, order_lines(qty, price_at_order, name_at_order, sku_at_order, unit_at_order, pack_size_at_order)')
        .in('company_id', companyIds)
        .order('created_at', { ascending: false })
      if (from) query = query.gte('created_at', `${from}T00:00:00Z`)
      if (to)   query = query.lte('created_at', `${to}T23:59:59Z`)
      const { data } = await query
      setOrders(data || [])
      setLoading(false)
    }
    run()
  }, [groupId, from, to])

  // Products actually ordered by this group in the chosen window — this is
  // what the product picker offers, never the full catalogue. Coupon /
  // discount lines carry no product so they're left out of the picker.
  const productOptions = useMemo(() => {
    const map = new Map()
    for (const o of orders) {
      for (const l of (o.order_lines || [])) {
        const sku = l.sku_at_order
        if (!sku || sku === 'COUPON') continue
        if (!map.has(sku)) map.set(sku, l.name_at_order || sku)
      }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [orders])

  // Prune lines to the selected product, drop orders left with none. Line-level
  // so a multi-product invoice only contributes the product being asked about.
  const scopedOrders = useMemo(() => {
    if (productFilter === '__all__') return orders
    return orders
      .map(o => ({ ...o, order_lines: (o.order_lines || []).filter(l => l.sku_at_order === productFilter) }))
      .filter(o => o.order_lines.length > 0)
  }, [orders, productFilter])

  useEffect(() => {
    if (productFilter !== '__all__' && !productOptions.some(([sku]) => sku === productFilter)) {
      setProductFilter('__all__')
    }
  }, [productOptions, productFilter])

  const summary = useMemo(() => {
    const bySite = {}
    const byProduct = new Map()
    let totalValue = 0
    let totalOrders = 0
    for (const o of scopedOrders) {
      const lineSum = (o.order_lines || []).reduce(
        (s, l) => s + Number(l.price_at_order || 0) * Number(l.qty || 0), 0
      )
      totalValue += lineSum
      totalOrders += 1
      if (!bySite[o.company_id]) bySite[o.company_id] = { count: 0, value: 0, products: new Map() }
      bySite[o.company_id].count += 1
      bySite[o.company_id].value += lineSum
      for (const l of (o.order_lines || [])) {
        // Group by SKU, not name. Sage appends things like "(100)" to the
        // item name inconsistently between invoices — grouping on name split
        // one product into two report rows.
        const key = l.sku_at_order || l.name_at_order || '(unknown)'
        const val = Number(l.price_at_order || 0) * Number(l.qty || 0)
        const prev = bySite[o.company_id].products.get(key) || { qty: 0, value: 0, unit: l.unit_at_order, pack: l.pack_size_at_order, sku: l.sku_at_order, name: l.name_at_order }
        prev.qty += Number(l.qty || 0)
        prev.value += val
        bySite[o.company_id].products.set(key, prev)

        // Group-wide roll-up, so a SKU can be totalled across every site
        // without expanding each one and adding up by hand.
        const g = byProduct.get(key) || { name: l.name_at_order || key, sku: l.sku_at_order, qty: 0, value: 0, siteIds: new Set(), orderIds: new Set() }
        g.qty += Number(l.qty || 0)
        g.value += val
        g.siteIds.add(o.company_id)
        g.orderIds.add(o.id)
        byProduct.set(key, g)
      }
    }
    const products = [...byProduct.values()]
      .map(p => ({ ...p, sites: p.siteIds.size, orders: p.orderIds.size }))
      .sort((a, b) => b.value - a.value)
    return { bySite, products, totalValue, totalOrders }
  }, [scopedOrders])

  const ordersForSite = (siteId) => scopedOrders.filter(o => o.company_id === siteId)

  const downloadCsv = () => {
    const rows = [['Reference', 'Site', 'Status', 'Placed', 'PO', 'Requested delivery', 'Lines', 'Value']]
    const siteName = id => sites.find(s => s.id === id)?.name || ''
    // scopedOrders, not orders — the CSV must match what's on screen,
    // including any product filter.
    for (const o of scopedOrders) {
      const lineSum = (o.order_lines || []).reduce(
        (s, l) => s + Number(l.price_at_order || 0) * Number(l.qty || 0), 0
      )
      rows.push([
        o.reference || '',
        siteName(o.company_id),
        o.status,
        o.placed_at || o.created_at,
        o.customer_po || '',
        o.delivery_date_requested || '',
        (o.order_lines || []).length,
        lineSum.toFixed(2),
      ])
    }
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const group = groups.find(g => g.id === groupId)?.name || 'group'
    a.download = `${group}-orders-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <DemoNotice />

      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-5 h-5 text-brand" />
          <h3 className="font-semibold text-gray-800">Reports</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Pull all orders across every site under a client group. Use date filters and download as CSV.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <label>
            <span className="text-xs text-gray-500 block mb-1">Client group</span>
            <select value={groupId} onChange={e => setGroupId(e.target.value)} className="input-field">
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">From</span>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="input-field" />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">To</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className="input-field" />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">Product</span>
            <select
              value={productFilter}
              onChange={e => setProductFilter(e.target.value)}
              disabled={productOptions.length === 0}
              className="input-field"
            >
              <option value="__all__">— All products ({productOptions.length}) —</option>
              {productOptions.map(([sku, name]) => <option key={sku} value={sku}>{name}</option>)}
            </select>
          </label>
          <div className="flex items-end">
            <button onClick={downloadCsv} disabled={scopedOrders.length === 0} className="btn-secondary w-full justify-center">
              Download CSV
            </button>
          </div>
        </div>

        {productFilter !== '__all__' && (
          <div className="mt-3 flex items-center gap-2 text-sm bg-brand-50 border border-brand/30 rounded-lg px-3 py-2">
            <Package className="w-4 h-4 text-brand-dark shrink-0" />
            <span className="text-gray-800">
              Filtered to <strong>{productOptions.find(([s]) => s === productFilter)?.[1] || productFilter}</strong> —
              totals, sites and CSV all reflect this product only.
            </span>
            <button
              onClick={() => setProductFilter('__all__')}
              className="ml-auto text-xs font-medium text-brand-dark hover:underline whitespace-nowrap"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {loading && <div className="card text-sm text-gray-500">Loading…</div>}

      {!loading && groupId && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="card">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Sites in group</div>
              <div className="text-2xl font-semibold text-gray-800 mt-1 flex items-center gap-2">
                <Layers className="w-5 h-5 text-brand" /> {sites.length}
              </div>
            </div>
            <div className="card">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Orders</div>
              <div className="text-2xl font-semibold text-gray-800 mt-1 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-brand" /> {summary.totalOrders}
              </div>
            </div>
            <div className="card">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Total value</div>
              <div className="text-2xl font-semibold text-gray-800 mt-1">{fmtMoney(summary.totalValue)}</div>
            </div>
          </div>

          {summary.products.length > 0 && (
            <div className="card">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-brand" />
                <h3 className="font-semibold text-gray-800">By product — whole group</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Every SKU totalled across all sites in this group, so you don't have to expand each site and add up by hand.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                      <th className="text-left py-2">Product</th>
                      <th className="text-right py-2 px-3 w-20 whitespace-nowrap">Sites</th>
                      <th className="text-right py-2 px-3 w-20 whitespace-nowrap">Orders</th>
                      <th className="text-right py-2 px-3 w-24 whitespace-nowrap">Qty</th>
                      <th className="text-right py-2 pl-3 w-28 whitespace-nowrap">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.products.map((p, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-2 pr-3">
                          <div className="font-medium text-gray-800">{p.name}</div>
                          {p.sku && <div className="text-xs text-gray-500">{p.sku}</div>}
                        </td>
                        <td className="text-right tabular-nums text-gray-700 px-3">{p.sites}</td>
                        <td className="text-right tabular-nums text-gray-700 px-3">{p.orders}</td>
                        <td className="text-right tabular-nums text-gray-700 px-3">
                          {Number.isInteger(p.qty) ? p.qty.toLocaleString('en-GB') : p.qty.toFixed(2)}
                        </td>
                        <td className="text-right tabular-nums font-medium text-gray-800 pl-3">{fmtMoney(p.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold text-gray-800">
                      <td className="pt-3 text-right pr-3" colSpan={4}>Total</td>
                      <td className="pt-3 text-right tabular-nums pl-3">{fmtMoney(summary.totalValue)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-1">By site</h3>
            <p className="text-xs text-gray-500 mb-3">Click a site to see its orders and product totals.</p>
            {sites.length === 0
              ? <p className="text-sm text-gray-500">No sites in this group.</p>
              : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                      <th className="text-left py-2 w-8"></th>
                      <th className="text-left py-2">Site</th>
                      <th className="text-right py-2">Orders</th>
                      <th className="text-right py-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sites.map(s => {
                      const row = summary.bySite[s.id] || { count: 0, value: 0, products: new Map() }
                      const isOpen = expandedSite === s.id
                      const siteOrders = isOpen ? ordersForSite(s.id) : []
                      const products = isOpen
                        ? Array.from(row.products.entries())
                            .map(([name, info]) => ({ name, ...info }))
                            .sort((a, b) => b.qty - a.qty)
                        : []
                      return (
                        <Fragment key={s.id}>
                          <tr
                            onClick={() => setExpandedSite(isOpen ? null : s.id)}
                            className={`border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${row.count === 0 ? 'text-gray-400' : ''}`}
                          >
                            <td className="py-2">
                              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </td>
                            <td className="py-2">{s.name}</td>
                            <td className="py-2 text-right">{row.count}</td>
                            <td className="py-2 text-right font-medium">{fmtMoney(row.value)}</td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-gray-50/60">
                              <td colSpan={4} className="p-4">
                                {row.count === 0 ? (
                                  <p className="text-sm text-gray-500">No orders for this site in the selected period.</p>
                                ) : (
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div>
                                      <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                                        <Package className="w-4 h-4 text-brand" /> Products ordered
                                      </h4>
                                      <ul className="divide-y divide-gray-200">
                                        {products.map(p => (
                                          <li key={p.name} className="py-1.5 flex items-center gap-2 text-sm">
                                            <div className="flex-1 min-w-0">
                                              <div className="font-medium text-gray-800 truncate">{p.name}</div>
                                              {p.sku && <div className="text-xs text-gray-500">{p.sku}{p.pack ? ` · ${p.pack}/box` : ''}</div>}
                                            </div>
                                            <div className="text-right">
                                              <div className="font-medium text-gray-800">{p.qty}{p.unit ? ` ${p.unit}` : ''}</div>
                                              <div className="text-xs text-gray-500">{fmtMoney(p.value)}</div>
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                    <div>
                                      <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                                        <ClipboardList className="w-4 h-4 text-brand" /> Orders
                                      </h4>
                                      <ul className="divide-y divide-gray-200">
                                        {siteOrders.map(o => {
                                          const v = (o.order_lines || []).reduce(
                                            (sum, l) => sum + Number(l.price_at_order || 0) * Number(l.qty || 0), 0
                                          )
                                          return (
                                            <li key={o.id} className="py-1.5">
                                              <Link to={`/orders/${o.id}`} className="flex items-center gap-2 text-sm hover:bg-white -mx-2 px-2 py-1 rounded">
                                                <div className="flex-1 min-w-0">
                                                  <div className="font-medium text-gray-800 truncate">{o.reference || '(draft)'}</div>
                                                  <div className="text-xs text-gray-500 truncate">
                                                    {o.status} · {(o.placed_at ? new Date(o.placed_at) : new Date(o.created_at)).toLocaleDateString()}
                                                    {o.customer_po ? ` · PO ${o.customer_po}` : ''}
                                                  </div>
                                                </div>
                                                <div className="text-sm font-medium text-gray-700">{fmtMoney(v)}</div>
                                              </Link>
                                            </li>
                                          )
                                        })}
                                      </ul>
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              )
            }
          </div>

          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-3">Orders ({orders.length})</h3>
            {orders.length === 0
              ? <p className="text-sm text-gray-500">No orders in the selected period.</p>
              : (
                <ul className="divide-y divide-gray-100">
                  {orders.map(o => {
                    const value = (o.order_lines || []).reduce(
                      (s, l) => s + Number(l.price_at_order || 0) * Number(l.qty || 0), 0
                    )
                    const siteName = sites.find(s => s.id === o.company_id)?.name || '—'
                    return (
                      <li key={o.id} className="py-2">
                        <Link to={`/orders/${o.id}`} className="flex items-center gap-3 hover:bg-gray-50 -mx-2 px-2 py-1 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-800 truncate">{o.reference || '(draft)'} — {siteName}</div>
                            <div className="text-xs text-gray-500 truncate">
                              {o.status} · {o.placed_at ? new Date(o.placed_at).toLocaleDateString() : new Date(o.created_at).toLocaleDateString()}
                              {o.customer_po ? ` · PO ${o.customer_po}` : ''}
                            </div>
                          </div>
                          <div className="text-sm font-medium text-gray-700">{fmtMoney(value)}</div>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              )
            }
          </div>
        </>
      )}
    </div>
  )
}
